import { z } from 'zod';
import { SperrbareAddonsSchema, type SperrbaresAddon } from './stripeAbrechnung';

/* Pakete und Abrechnung der Marktplätze durch DXL. Alle Beträge in Cent (Ganzzahl). */

const Cent = z.number({ invalid_type_error: 'Bitte Betrag angeben' }).int('Betrag in Cent als Ganzzahl').min(0, 'Nicht negativ').max(10_000_000_00);
const Anzahl = z.number({ invalid_type_error: 'Bitte Zahl angeben' }).int('Bitte ganze Zahl angeben').min(0, 'Nicht negativ').max(1_000_000);
const Prozent = z
  .number({ invalid_type_error: 'Bitte Prozentsatz angeben' })
  .min(0, 'Nicht negativ')
  .max(100, 'Höchstens 100 %')
  .refine((x) => Math.abs(Math.round(x * 100) - x * 100) < 1e-6, 'Höchstens zwei Nachkommastellen');

export const PROVISION_BASIS = { umsatz: 'Marktplatzumsatz (brutto)', provision: 'Provision des Marktplatzes' } as const;
export type ProvisionBasis = keyof typeof PROVISION_BASIS;

export const KonditionenSchema = z.object({
  grundgebuehr: Cent,
  provisionSatz: Prozent,
  provisionBasis: z.enum(['umsatz', 'provision']),
  inklHaendler: Anzahl,
  preisHaendler: Cent,
  inklAnfragen: Anzahl,
  preisAnfrage: Cent,
  einrichtung: Cent,
  whitelabel: z.boolean(),
  laufzeitMonate: Anzahl,
  testphaseTage: Anzahl,
});
export type Konditionen = z.infer<typeof KonditionenSchema>;
export type KonditionFeld = keyof Konditionen;

/** Feld → Typ und Bezeichnung (wie DXL_KONDITIONEN in der PHP-Middleware) */
export const KONDITIONEN: Record<KonditionFeld, { typ: 'euro' | 'prozent' | 'zahl' | 'ja' | 'basis'; titel: string }> = {
  grundgebuehr: { typ: 'euro', titel: 'Grundgebühr je Monat (netto)' },
  provisionSatz: { typ: 'prozent', titel: 'Provision' },
  provisionBasis: { typ: 'basis', titel: 'Provision berechnet auf' },
  inklHaendler: { typ: 'zahl', titel: 'Inklusive Händler (0 = unbegrenzt)' },
  preisHaendler: { typ: 'euro', titel: 'Je weiterem aktiven Händler / Monat' },
  inklAnfragen: { typ: 'zahl', titel: 'Inklusive Ankaufanfragen / Monat' },
  preisAnfrage: { typ: 'euro', titel: 'Je weitere Ankaufanfrage' },
  einrichtung: { typ: 'euro', titel: 'Einrichtungsgebühr (einmalig)' },
  whitelabel: { typ: 'ja', titel: 'Whitelabel enthalten' },
  laufzeitMonate: { typ: 'zahl', titel: 'Mindestlaufzeit (Monate)' },
  testphaseTage: { typ: 'zahl', titel: 'Testphase (Tage)' },
};

export const LEERE_KONDITIONEN: Konditionen = {
  grundgebuehr: 0, provisionSatz: 0, provisionBasis: 'umsatz', inklHaendler: 0, preisHaendler: 0,
  inklAnfragen: 0, preisAnfrage: 0, einrichtung: 0, whitelabel: false, laufzeitMonate: 0, testphaseTage: 0,
};

/* ------------------------------------------------------------------ Pakete */

export const PaketSchema = z.object({
  name: z.string().trim().min(1, 'Bitte einen Namen für das Paket angeben').max(80),
  beschreibung: z.string().trim().max(500).default(''),
  aktiv: z.boolean().default(true),
  empfohlen: z.boolean().default(false),
  konditionen: KonditionenSchema,
  /** im Paket enthaltene Add-ons (Whitelabel über die Konditionen) */
  addons: SperrbareAddonsSchema,
});
export type PaketEingabe = z.input<typeof PaketSchema>;

export interface Paket {
  id: string;
  name: string;
  beschreibung: string;
  aktiv: boolean;
  empfohlen: boolean;
  konditionen: Konditionen;
  addons: SperrbaresAddon[];
  /** Namen der Marktplätze mit diesem Paket */
  nutzung: string[];
  stripe: import('./stripeAbrechnung').StripeSyncStand;
}

/* ------------------------------------------------------------------ Vertrag */

const Datum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als JJJJ-MM-TT').nullable();
const Text = (max: number) => z.string().trim().max(max).default('');

export const VertragSchema = z.object({
  paketId: z.string().uuid().nullable(),
  /** nur abweichende Werte – alles andere kommt aus dem Paket */
  eigen: KonditionenSchema.partial().default({}),
  steuer: z.enum(['inland', 'reverse_charge']).default('inland'),
  abrechnungAb: Datum.default(null),
  vertragBeginn: Datum.default(null),
  vertragEnde: Datum.default(null),
  testBis: Datum.default(null),
  kundennummer: Text(40),
  kuendigungsfrist: Text(60),
  kontaktName: Text(120),
  kontaktMail: z.union([z.literal(''), z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190)]).default(''),
  kontaktTelefon: Text(60),
  rechnungMail: z.union([z.literal(''), z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190)]).default(''),
  notiz: Text(4000),
  /** von DXL erzwungene Freischaltungen – unabhängig von Paket und Buchung */
  freischaltungen: SperrbareAddonsSchema,
});
export type VertragEingabe = z.input<typeof VertragSchema>;
export type Vertrag = z.infer<typeof VertragSchema>;

export type KonditionHerkunft = 'individuell' | 'paket' | 'leer';

export interface GueltigeKonditionen {
  werte: Konditionen;
  herkunft: Record<KonditionFeld, KonditionHerkunft>;
  paket: { id: string; name: string } | null;
}

export interface VertragAntwort {
  mandant: { id: string; kuerzel: string; name: string; lizenz: 'standard' | 'whitelabel' };
  vertrag: Vertrag;
  konditionen: GueltigeKonditionen;
  /** Freischaltung der Add-ons: Bestandsschutz (kein selbst gebuchtes Paket) oder je Funktion aktiv/gesperrt */
  addons: AddonStand;
  geaendertAm: string | null;
}

/** Wirksame Freischaltungen eines Marktplatzes */
export interface AddonStand {
  /** true = Marktplatz hat selbst ein Paket gebucht, nur Enthaltenes, Gebuchtes und Freigeschaltetes ist frei */
  gesteuert: boolean;
  aktiv: SperrbaresAddon[];
}

/** Zeile der Marktplatz-Liste unter „Pakete & Abrechnung“ */
export interface VertragZeile {
  mandantId: string;
  kuerzel: string;
  name: string;
  status: 'aktiv' | 'eingeladen' | 'gesperrt';
  paket: string | null;
  individuell: number;
  grundgebuehr: number;
  kundennummer: string;
  rechnungMail: string;
  offen: number;
  abo: import('./stripeAbrechnung').AboStatus | null;
}

export interface VertragSeite {
  eintraege: VertragZeile[];
  weiter: string | null;
}

/* ------------------------------------------------------------------ Rechnungsdaten (Absender) */

export const RechnungsdatenSchema = z.object({
  firma: Text(160),
  strasse: Text(160),
  ort: Text(120),
  land: Text(60),
  ustId: Text(40),
  steuernummer: Text(40),
  register: Text(160),
  geschaeftsfuehrung: Text(160),
  email: Text(190),
  telefon: Text(60),
  web: Text(190),
  bank: Text(120),
  iban: Text(40),
  bic: Text(20),
  fusszeile: Text(500),
  ustSatz: z.number().min(0).max(30).default(19),
  zahlungsziel: z.number().int().min(0).max(90).default(14),
  praefix: z.string().trim().toUpperCase().regex(/^[A-Z0-9-]{1,10}$/, 'Nur Buchstaben, Ziffern und Bindestrich').default('DXL'),
  /** Monatslauf täglich selbst starten */
  automatisch: z.boolean().default(true),
  /** neue Rechnungen gleich per E-Mail senden */
  versand: z.boolean().default(true),
});
export type Rechnungsdaten = z.infer<typeof RechnungsdatenSchema>;
export const STANDARD_RECHNUNGSDATEN: Rechnungsdaten = RechnungsdatenSchema.parse({});

/* ------------------------------------------------------------------ Rechnungen */

export const RECHNUNG_STATUS = { offen: 'Offen', bezahlt: 'Bezahlt', storniert: 'Storniert' } as const;
export type RechnungStatus = keyof typeof RECHNUNG_STATUS;

export const Monat = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Monat als JJJJ-MM');

export interface Position {
  text: string;
  betrag: number;
  einrichtung?: boolean;
}

export interface Kennzahlen {
  umsatz: number;
  provision: number;
  bestellungen: number;
  haendler: number;
  anfragen: number;
}

export interface Berechnung {
  positionen: Position[];
  netto: number;
  ustSatz: number;
  ust: number;
  brutto: number;
  reverseCharge: boolean;
  kennzahlen: Kennzahlen;
  konditionen: GueltigeKonditionen;
}

export interface Empfaenger {
  name: string;
  marktplatz: string;
  strasse: string;
  ort: string;
  land: string;
  ustId: string;
  kundennummer: string;
  mail: string;
}

export interface RechnungZeile {
  id: string;
  nr: string;
  mandantId: string;
  marktplatz: string;
  kuerzel: string;
  monat: string;
  datum: string;
  faellig: string;
  netto: number;
  brutto: number;
  status: RechnungStatus;
  versendetAm: string | null;
  versandFehler: string | null;
}

export interface RechnungDetail extends RechnungZeile {
  positionen: Position[];
  ustSatz: number;
  ust: number;
  reverseCharge: boolean;
  paket: string;
  kennzahlen: Kennzahlen;
  empfaenger: Empfaenger;
  bezahltAm: string | null;
  versendetAn: string | null;
  erstelltVon: string;
  erstelltAm: string;
}

export interface RechnungenSeite {
  eintraege: RechnungZeile[];
  weiter: string | null;
  /** offene Rechnungen über alle Seiten (Anzahl und Summe brutto) */
  offen: { anzahl: number; summe: number };
}

export const RechnungenAbfrage = z.object({
  mandant: z.string().uuid().optional(),
  status: z.enum(['offen', 'bezahlt', 'storniert']).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const RechnungErstellenSchema = z.object({ mandantId: z.string().uuid(), monat: Monat });
export const MonatslaufSchema = z.object({ monat: Monat.optional() });
export const RechnungStatusSchema = z.object({ status: z.enum(['offen', 'bezahlt', 'storniert']) });

export interface Versandergebnis {
  ok: boolean;
  an: string;
  fehler?: string;
}

/** Abrechnung im Konto des Marktplatzes (Verwaltung → Rechnungen) */
export interface MeineAbrechnung {
  konditionen: GueltigeKonditionen;
  vertrag: Pick<Vertrag, 'vertragBeginn' | 'vertragEnde' | 'testBis' | 'kuendigungsfrist' | 'rechnungMail' | 'kundennummer'>;
}

/* ------------------------------------------------------------------ Beträge */

/** 123456 → „1.234,56 €“ */
export function euro(cent: number): string {
  return (cent / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/** „1.234,56“, „490“, „19,9“ → Cent; ungültig → null */
export function euroZuCent(text: string): number | null {
  const t = text.trim().replace(/\s|€/g, '');
  if (!t) return 0;
  if (!/^\d{1,3}(\.\d{3})*(,\d{1,2})?$|^\d+([.,]\d{1,2})?$/.test(t)) return null;
  const normal = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : /^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, '') : t;
  const [ganz, rest = ''] = normal.split('.');
  return Number(ganz) * 100 + Number((rest + '00').slice(0, 2));
}

/** Cent → Eingabetext „1234,56“ */
export function centZuEingabe(cent: number): string {
  return (cent / 100).toFixed(2).replace('.', ',');
}

/** „2026-09“ → „09/2026“ */
export function monatText(monat: string): string {
  return `${monat.slice(5, 7)}/${monat.slice(0, 4)}`;
}
